import { type Category, type FieldType, type WidgetUid } from "../types";
import type FieldId from "./FieldId";
import { type Issue } from "../state/validation";
import type { TranslationKey } from "../state/i18n";
import type { FormScope, FormVersion, RuleConditionKey } from "../state/formVersion";
import type FormId from "../state/FormId";
import type { GenericSchema, GenericSchemaAsync } from "valibot";
import type { IconName } from "../components/icon/Icon";
import type { JSX, VoidComponent } from "solid-js";
import type VariableId from "../variables/VariableId";
import type { VariableType } from "../variables/types";
import type { Favourite } from "../services/FavouriteService";
import type { FilterDefinition, FilterType } from "../pages/customer/form/filter";
import type { DynamicValueOption } from "../types/Menu";
import type FieldValueReference from "./FieldValueReference";
import type { StringTemplateTypes } from "../utils/stringTemplateUtil.ts";

export type MenuItemProps = { addFunction: (type: FieldType) => void };

export type FavouriteMenuItemProps = {
  addFunction: (type: FieldType) => void;
  label: string;
  favourite: Favourite;
  onEdit: (value: string) => void;
  onDelete: () => void;
};

export type PreviewItemProps = { uid: FieldId };

export type EditItemProps<T> = {
  uid: FieldId;
  data: FieldConfig<T>;
  setValue: (property: keyof T, value: unknown) => void;
  getError: (property: keyof T) => string | undefined;
  getNestedErrors: (property: keyof T) => Issue[] | undefined; // Only for fields with nested properties, like the search field
  getGlobalErrors: () => Issue[] | undefined;
  propertyKeys: Record<keyof T, TranslationKey>;
};

export type PreviewContentProps<T> = {
  uid: FieldId;
  properties: FieldConfig<T>["properties"];
};

export type Label = "default" | "no_label" | "not_required";

export type FieldState = "unpublished" | "published" | "unknown";

export type FieldDataNameProperties = { data_name?: string };

export type FieldLabelTextProperties = { label_text?: string };

export type FieldConfig<T = Record<string, unknown>> = {
  type: FieldType;
  uid: FieldId;
  properties: T;
  scope: FormScope;
  state: FieldState;
};

export enum VariableState {
  PUBLISHED = "published",
  UNPUBLISHED = "unpublished",
  UNKNOWN = "unknown",
}

export type VariableConfig<T = unknown> = {
  uid: VariableId;
  name: string;
  dataName: string;
  dataNameManuallyEdited?: boolean;
  type: VariableType;
  defaultValue: T;
  state: VariableState;
};

export type UnknownFieldConfig = FieldConfig<FieldDataNameProperties & FieldLabelTextProperties> & {
  type: "unknown";
  widget: WidgetUid;
};

export type RuleConditionVariant = "text" | "boolean" | "select" | "dataSource" | "none";

export type RuleActionVariant = "text" | "boolean" | "select" | "date" | "time" | "dateTime" | "none";

export type RuleActionBehaviour = {
  setValue?: {
    fieldTypes: FieldType[];
    variableTypes: VariableType[];
  };
  variant: RuleActionVariant;
};

export type RuleConditionBehaviour = {
  keys: RuleConditionKey[];
  variant: RuleConditionVariant;
};

type RuleBehaviour = {
  conditions: RuleConditionBehaviour;
  actions: RuleActionBehaviour;
};

export type ResourceSchema = GenericSchemaAsync | ((formId: FormId) => GenericSchemaAsync);

export type BaseField<T, DTO, V = unknown> = {
  type: FieldType;
  category: Category;
  widget: WidgetUid;
  canAddToForm: () => boolean;
  schema: GenericSchema<T | unknown, T>;
  resourceSchema?: ResourceSchema; // Use for asynchronous validation only
  dtoSchema?: GenericSchema<DTO>;
  valueSchema?: GenericSchema<unknown, V>;
  defaultProperties: () => Partial<T>;
  duplicateProperties: (
    properties: T,
    updateReferences: (stringTemplateType: StringTemplateTypes) => StringTemplateTypes,
  ) => T;
  ruleBehaviour?: RuleBehaviour;
  hasData: boolean;
  filter?: {
    type: FilterType;
    definition: FilterDefinition[];
  };
  stringValue?: (value: V, path?: string[]) => string;
  renderValue?: (
    value: V,
    context: {
      fieldConfig: FieldConfig<T>;
      formVersion: FormVersion;
    },
  ) => JSX.Element;
  label: Label;
  displayLabel: (props: T & FieldLabelTextProperties) => string;
  init: (scope: FormScope, props?: Partial<T>) => FieldConfig<Partial<T>>;
  icon: IconName;
  MenuItem: VoidComponent<MenuItemProps>;
  FavouriteMenuItem: VoidComponent<FavouriteMenuItemProps>;
  PreviewItem: VoidComponent<PreviewItemProps>;
  EditItem: VoidComponent<EditItemProps<T>>;
  OverlayItem: VoidComponent;
  PreviewContent: VoidComponent<PreviewContentProps<T>>;
  titleKey: TranslationKey;
  descriptionKey: TranslationKey;
  propertyKeys: Record<keyof T, TranslationKey>;
  fromDto: (props: DTO) => T;
  toDto: (props: T, allFields: FieldConfig[], variables: VariableConfig[]) => DTO;
  postProcessFromDto: (props: T, allFields: FieldConfig[], variables: VariableConfig[], scope: FormScope) => T;
  valueReferenceOptions: (
    id: FieldId,
    props: T & FieldDataNameProperties,
    options?: Partial<Record<FieldType, Record<string, unknown>>>,
  ) => (DynamicValueOption<FieldValueReference> & { isSelectable?: boolean })[];
};
